import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Paper, IconButton, Tooltip, Box, Typography, Alert } from "@mui/material";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { pdf } from "@react-pdf/renderer"; // Added pdf import here
import { ApiService } from "../../ApiService";
import pdfIcon from "../../../images/pdf.png";
import buscar_azul from "../../../images/search_azul.png";
import ErrorIcon from "@mui/icons-material/Error";
import { GeneratePdfAllOrdenVentas } from "../../../Reports/GeneratePdfAllOrdenVentas";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import { useParams } from "react-router-dom";
import LupaIcon from "../../../svg/lupaIcon";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";
import SearchCompany from "../../../pages/empresa_uno/SearchCompany";

export const VventasDia = ({ selectedSucursal, permisos, convertPrice = (() => { }) }) => {
	const { nit } = useParams();
	const [objCurrentSales, setObjCurrentSales] = useState([]);
	const [processedSales, setProcessedSales] = useState([]);
	const [loadingCancelId, setLoadingCancelId] = useState(null);

	const fetchCurrentVentas = async (key_branch) => {
		const url = `api/company/sale/current-list?key_branch=${key_branch}`;
		const options = { method: "GET" };

		const request_totals = await ApiService.fetchData(url, options);
		if (!request_totals) {
			toast.error("Error al cargar los datos de ventas del día");
			return;
		}
		const salesData = request_totals.map((item) => ({
			...item,
			id: item.authCode,
			totalPrice: (parseFloat(item.cost) + parseFloat(item.earning)).toFixed(2),
		}));

		setObjCurrentSales(salesData);
		const { processedData } = processData(salesData);
		setProcessedSales(processedData);
	};
	const handleExportAllSalePDF = async () => {
		try {
			const url = `api/company/sale/pdf-today?key_branch=${selectedSucursal}`;
			const options = { method: "GET" };
			const saleOrderData = await ApiService.fetchData(url, options);
			if (saleOrderData) {
				const MyDoc = <GeneratePdfAllOrdenVentas saleData={saleOrderData} themeColor={'var(--theme-company)'} convertPrice={convertPrice} />;
				const asPdf = pdf(MyDoc);
				const blob = await asPdf.toBlob();
				const fileURL = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = fileURL;
				link.download = `OrdenVenta_${Date.now().toLocaleString()}.pdf`;
				link.click();
				URL.revokeObjectURL(fileURL);
			} else {
				toast.error("No se encontró la información de la venta.");
			}
		} catch (error) { }
	};
	const processData = (salesData) => {
		// Sort by nro in descending order (newest first)
		const sortedData = [...salesData].sort((a, b) => b.nro - a.nro);

		// Keep track of unique nro values to mark first occurrences
		let currentNro = null;

		// Process each item to mark first occurrences of nro
		const processedData = sortedData.map((item) => {
			const isFirstInGroup = item.nro !== currentNro;
			if (isFirstInGroup) {
				currentNro = item.nro;
			}

			return {
				...item,
				isFirstInGroup,
			};
		});

		// Create a map of products by nro for PDF export
		const productsByNro = sortedData.reduce((acc, item) => {
			if (!acc.has(item.nro)) {
				acc.set(item.nro, {
					nro: item.nro,
					client_name: item.client_name,
					products: [],
				});
			}
			acc.get(item.nro).products.push(item);
			return acc;
		}, new Map());

		return { processedData, productsByNro };
	};
	useEffect(() => {
		fetchCurrentVentas(selectedSucursal);
	}, [selectedSucursal]);

	const columns = [
		{
			field: "nro",
			headerName: "N° Venta",
			flex: 1,
			renderCell: (params) => (params.row.isFirstInGroup ? params.value : ""),
		},
		{
			field: "client_name",
			headerName: "Cliente",
			flex: 2,
			renderCell: (params) => (params.row.isFirstInGroup ? params.value : ""),
		},
		{
			field: "code",
			headerName: "Código",
			flex: 1,
		},
		{
			field: "product_name",
			headerName: "Producto",
			flex: 2,
		},
		{
			field: "qp",
			headerName: "Cantidad",
			align: "center",
			headerAlign: "center",
			flex: 1,
		},
		...(!permisos?.remove_costo && !permisos?.remove_costo
			? [
				{
					field: "cost",
					headerName: "Costo",
					flex: 1,
					renderCell: (params) => <Box>{convertPrice(params.value, null, true)}</Box>,
				},
				{
					field: "earning",
					headerName: "Beneficio",
					flex: 1,
					renderCell: (params) => <Box>{convertPrice(params.value, null, true)}</Box>,
				},
			]
			: []),
		{
			field: "totalPrice",
			headerName: "Precio",
			flex: 1,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Box sx={{ display: "flex", justifyContent: "center" }}>{convertPrice(params.value, null, true)}</Box>
			),
		},
		{
			field: "actions",
			headerName: "Acciones",
			flex: 1,
			align: "center",
			headerAlign: "center",
			renderCell: (params) =>
				params.row.isFirstInGroup ? (
					<Box>
						<Tooltip title="Anular venta" arrow>
							<IconButton
								color="error"
								loading={loadingCancelId === params.row.authCode}
								onClick={() => anularVenta(params.row.authCode)}>
								<CancelRoundedIcon />
							</IconButton>
						</Tooltip>
					</Box>
				) : null,
		},
	];
	const totales = {
		cantidad: objCurrentSales.reduce((acc, venta) => acc + venta.qp, 0),
		costo: convertPrice(objCurrentSales.reduce((acc, venta) => parseFloat(acc) + parseFloat(venta.cost), 0)),
		beneficio: convertPrice(
			objCurrentSales.reduce((acc, venta) => parseFloat(acc) + parseFloat(venta.earning), 0)
		),
		precio: convertPrice(
			objCurrentSales.reduce((acc, venta) => parseFloat(acc) + parseFloat(venta.totalPrice), 0)
		),
	};
	const anularVenta = async (keySale) => {
		setLoadingCancelId(keySale);

		try {
			const body = {
				key_sale: keySale,
			};
			const url = `api/company/sale/cancel?key_branch=${selectedSucursal}`;
			const options = { method: "DELETE", body: JSON.stringify(body) };
			const request_totals = await ApiService.fetchData(url, options);

			if (!request_totals) toast.error("Error al anular la venta");
			if (request_totals.code === 200) {
				toast.success("Venta anulada correctamente");
				setLoadingCancelId(null);
				await fetchCurrentVentas(selectedSucursal);
			}
		} catch (error) {
			toast.error("Error al anular la venta");
		} finally {
			setLoadingCancelId(null);
		}
	};
	const CustomFooter = () => {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					bgcolor: 'var(--theme-company)',
					color: "white",
					p: 1,
					mt: 1,
				}}>
				{/* N° Venta column - Total label */}
				<Box sx={{ flex: 1 }}></Box>

				{/* Cliente column - empty */}
				<Box sx={{ flex: 2 }}></Box>

				{/* Código column - empty */}
				<Box sx={{ flex: 1 }}></Box>

				{/* Producto column - empty */}
				<Box sx={{ flex: 2 }}>
					<Typography variant="caption" fontWeight="bold">
						Total
					</Typography>
				</Box>

				{/* Cantidad column - total quantity */}
				<Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
					<Typography variant="caption" fontWeight="bold">
						{totales.cantidad}
					</Typography>
				</Box>

				{/* Conditional Cost/Beneficio columns */}
				{!permisos?.remove_costo && !permisos?.remove_costo && (
					<>
						<Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start", pl: 1 }}>
							<Typography variant="caption" fontWeight="bold">
								{totales.costo}
							</Typography>
						</Box>
						<Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start", pl: 1 }}>
							<Typography variant="caption" fontWeight="bold">
								{totales.beneficio}
							</Typography>
						</Box>
					</>
				)}

				{/* Precio column - total price */}
				<Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
					<Typography variant="caption" fontWeight="bold">
						{totales.precio}
					</Typography>
				</Box>

				{/* Actions column - empty */}
				<Box sx={{ flex: 1 }}></Box>
			</Box>
		);
	};
	const NoDataOverlay = () => {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					height: "100%",
				}}>
				<ErrorIcon />
				<Typography variant="h6" color="textSecondary">
					No hay ventas registradas para hoy.
				</Typography>
			</Box>
		);
	};
	return (
		<div>
			<div className="functionality-container">
				<div className="header-reports">
					<div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
						<h2 style={{ color: 'var(--theme-company)' }}>Ventas del día</h2>
						<h2 style={{ color: 'var(--theme-company)' }}>{new Date().toLocaleDateString("es-ES")}</h2>
					</div>

					<div>
						<SearchCompany theme={'var(--theme-company)'} placeholder="venta" />

						<div>
							<Button variant="contained" onClick={handleExportAllSalePDF} sx={{ height: 41, background: 'var(--theme-company)' }} startIcon={<PictureAsPdfIcon />} >
								<span style={{ color: '#fff' }}>PDF</span>
							</Button>
						</div>
					</div>
				</div>
			</div>

			<Paper sx={{ width: "100%", height: 500 }}>
				<DataGrid
					rows={processedSales}
					columns={columns}
					autosizeOnMount={true}
					disableColumnMenu={true}
					pageSize={10}
					disableRowSelectionOnClick
					getRowClassName={(params) => (params.row.isFirstInGroup ? "firstInGroup" : "")}
					initialState={{
						sorting: {
							sortModel: [{ field: "nro", sort: "asc" }],
						}
					}}
					slots={{
						footer: CustomFooter,
						noRowsOverlay: NoDataOverlay,
					}}
					sx={{



						"& .MuiDataGrid-row": {
							"&.firstInGroup": {
								borderTop: "2px solid #bbdefb",
							},
						},
						"& .MuiDataGrid-columnHeader": {
							backgroundColor: 'var(--theme-company)',
							color: "white",
							fontWeight: "bold",
						},
					}}
					components={{ Toolbar: GridToolbar }}
				/>
			</Paper>
		</div>
	);
};
